import { Lane, LaneDisplayNames } from "./Shared.Lane.gen";
import { LaneLine } from "./LaneLine.gen";

// Maps a Lane bit value (1, 2, 4, …) to its lane number (1-based).
const bitToLaneNumber = (bit: number): number => Math.log2(bit) + 1;

export interface ConfiguredLaneName {
    // Comma-joined configured names (with "Lane N" fallback for unnamed bits).
    primary: string;
    // The original "Lane 1, Lane 2" label — useful for displaying alongside.
    rawLabel: string;
    // True if at least one bit had a user-configured name.
    hasConfiguredName: boolean;
}

// Decode a Lane bitmask into configured names for a given device.
// `laneLines` should be the LaneLines for the target's device (caller filters by deviceId).
// Falls back to "Lane N" per-bit when no name is configured.
export const getConfiguredLaneName = (lanes: Lane | number | undefined, laneLines: LaneLine[] | undefined): ConfiguredLaneName | null => {
    if (!lanes) return null;

    // Multiple lane lines can map to one lane number (e.g. facility-scoped plus device-scoped).
    // Collect distinct names per lane number, preserving order.
    const namesByLaneNumber = new Map<number, string[]>();
    laneLines?.forEach((ll) => {
        if (ll.laneNumber != null && ll.name) {
            const names = namesByLaneNumber.get(ll.laneNumber) ?? [];
            if (!names.includes(ll.name)) names.push(ll.name);
            namesByLaneNumber.set(ll.laneNumber, names);
        }
    });

    const bits = Object.keys(LaneDisplayNames)
        .map((k) => parseInt(k))
        .filter((v) => !isNaN(v) && v > 0 && (v & Number(lanes)) > 0);

    if (!bits.length) return null;

    let hasConfiguredName = false;
    const primaryParts: string[] = [];
    const rawParts: string[] = [];
    for (const bit of bits) {
        const laneNumber = bitToLaneNumber(bit);
        const rawLabel = `Lane ${laneNumber}`;
        const configured = namesByLaneNumber.get(laneNumber);
        if (configured?.length) hasConfiguredName = true;
        primaryParts.push(configured?.length ? configured.join(", ") : rawLabel);
        rawParts.push(rawLabel);
    }

    return {
        primary: primaryParts.join(", "),
        rawLabel: rawParts.join(", "),
        hasConfiguredName,
    };
};
